import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConsultationModal from "@/components/ConsultationModal";
import { ArrowRight, Calculator, TrendingDown } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryLabels = {
  india: "India", usa: "USA", uk: "UK", uae: "UAE", singapore: "Singapore",
  saudi_arabia: "Saudi Arabia", nigeria: "Nigeria", kenya: "Kenya",
  ghana: "Ghana", south_africa: "South Africa", mauritius: "Mauritius"
};

function calcSavings(indiaRange, otherRange) {
  if (!otherRange || otherRange === "N/A") return "N/A";
  try {
    const parseAvg = (r) => { const nums = r.replace(/[^0-9,.-]/g, "").split("-").map(n => parseFloat(n.replace(/,/g, ""))); return (nums[0] + (nums[1] || nums[0])) / 2; };
    const indiaAvg = parseAvg(indiaRange);
    const otherAvg = parseAvg(otherRange);
    if (otherAvg <= 0) return "N/A";
    const pct = Math.round(((otherAvg - indiaAvg) / otherAvg) * 100);
    return pct > 0 ? `${pct}%` : "N/A";
  } catch { return "N/A"; }
}

export default function CostComparisonPage() {
  const [data, setData] = useState([]);
  const [compareCountry, setCompareCountry] = useState("usa");

  useEffect(() => {
    axios.get(`${API}/cost-comparison`).then(r => setData(r.data)).catch(console.error);
  }, []);

  return (
    <div data-testid="cost-comparison-page">
      <section className="bg-gradient-to-b from-orange-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4 font-body text-xs uppercase tracking-wider">
            <Calculator className="h-3 w-3 inline mr-1" /> Cost Calculator
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-4" data-testid="cost-comparison-title">Treatment Cost Comparison</h1>
          <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto mb-8">See how much you can save by choosing India for your medical treatment. Real prices, transparent comparison.</p>
          <div className="inline-flex items-center gap-3 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200">
            <span className="text-sm text-slate-600 font-medium">Compare India with:</span>
            <Select value={compareCountry} onValueChange={setCompareCountry}>
              <SelectTrigger className="w-40 border-0 shadow-none" data-testid="country-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(countryLabels).filter(([k]) => k !== "india").map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-4 px-4 text-sm font-semibold text-slate-500 uppercase tracking-wider">
              <div className="col-span-4">Treatment</div>
              <div className="col-span-3 text-center">India</div>
              <div className="col-span-3 text-center">{countryLabels[compareCountry]}</div>
              <div className="col-span-2 text-center">Savings</div>
            </div>

            {data.map((row, i) => {
              const otherCost = row[compareCountry] || "N/A";
              const dynamicSavings = calcSavings(row.india, otherCost);
              return (
                <Card key={i} className="bg-white rounded-xl border border-slate-100 p-4 hover:border-orange-200 transition-colors" data-testid={`cost-row-${i}`}>
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <h3 className="font-semibold text-slate-900 text-sm">{row.treatment}</h3>
                    </div>
                    <div className="col-span-3 text-center">
                      <span className="text-orange-600 font-bold text-sm">{row.india}</span>
                    </div>
                    <div className="col-span-3 text-center">
                      <span className="text-slate-500 text-sm">{otherCost}</span>
                    </div>
                    <div className="col-span-2 text-center">
                      {dynamicSavings !== "N/A" ? (
                        <Badge className="bg-orange-100 text-orange-700 border-0 text-xs font-bold">
                          <TrendingDown className="h-3 w-3 mr-1" />
                          {dynamicSavings}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs">N/A</span>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12 text-sm text-slate-400">
            <p>* Costs are indicative ranges based on standard procedures. Actual costs may vary based on complexity, hospital, and room category.</p>
            <p className="mt-1">* India costs include hospital charges, surgeon fees, implants/consumables, and standard hospital stay.</p>
          </div>

          <div className="bg-slate-900 rounded-3xl p-10 md:p-14 text-center mt-16">
            <h2 className="font-heading text-3xl font-semibold text-white mb-4">Get Your Personalized Cost Estimate</h2>
            <p className="text-slate-300 max-w-xl mx-auto mb-8 text-sm">Share your medical details and receive a detailed, transparent cost estimate within 24 hours. No hidden charges, no surprises.</p>
            <ConsultationModal
              trigger={
                <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 font-semibold text-base" data-testid="cost-cta-btn">
                  Get Free Estimate <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              }
            />
          </div>
        </div>
      </section>
    </div>
  );
}
